<script setup lang="ts">
import { computed, nextTick, provide, ref, unref, type MaybeRef } from 'vue'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import ColumnMapping from '@/components/ColumnMapping.vue'
import ColumnHeader from '@/components/DataTable/ColumnHeader.vue'
import { MIN_COLUMN_WIDTH_PX } from '@/components/DataTable/minColumnWidth'
import { classifyRowClick, ROW_CLICK_IGNORE_SELECTOR, ROW_OPENS_IN_PEEK } from '@/components/DataTable/rowClick'
import { useProgressiveRows } from '@/composables/useProgressiveRows'
import { groupHeadersFor } from '@/components/DataTable/groupBoundaries'
import ListGroupHeader from '@/components/DataTable/ListGroupHeader.vue'
import type { TableColumn } from '@/types/tableTypes'

const {
  data,
  rowIdKey,
  selectedRowId,
  columns = [],
  pinnedColumns = [],
  stickyHeader,
  isLoading,
  tableInParentScroll = false,
  isTableBodyRowHidden,
  isRowMissing,
  selectable = false,
  isRowSelected,
  pageSelectionState = false,
  groupByField = null,
  onRowClick,
  isRowOpenable,
} = defineProps<{
  data?: Record<string, unknown>[];
  totalRecords?: number;
  columns?: MaybeRef<TableColumn[]>;
  pinnedColumns?: MaybeRef<TableColumn[]>;
  stickyHeader?: boolean;
  /** Parent provides vertical scroll (e.g. toolbar stays fixed above scroll area). */
  tableInParentScroll?: boolean;
  isLoading?: boolean;
  pageIndex?: number;
  pageSize?: number;
  /** When true, the entire body row is not displayed (e.g. Raw DDEX rows kept mounted but collapsed). */
  isTableBodyRowHidden?: (row: Record<string, unknown>) => boolean;
  /**
   * Live polling no longer finds this row (deleted / de-scoped): dim it and
   * keep its last-known values until the next real search removes it.
   */
  isRowMissing?: (row: Record<string, unknown>) => boolean;
  /**
   * Row-selection highlight: when `rowIdKey` is set, the row whose
   * `row[rowIdKey]` equals `selectedRowId` is highlighted (e.g. the row whose
   * inspector side-peek is open). Unset on other tables → no behaviour change.
   */
  rowIdKey?: string;
  selectedRowId?: string | number | null;
  /**
   * Draw a leading checkbox column for bulk selection.
   * It is not a data column, so it never shows up in Column Settings or in a saved view.
   */
  selectable?: boolean;
  /** Whether a row's checkbox is ticked. Only read when `selectable` is on. */
  isRowSelected?: (row: Record<string, unknown>) => boolean;
  /** The header checkbox: every row on the page ticked, none, or some. */
  pageSelectionState?: boolean | 'indeterminate';
  /**
   * Group by: the field whose value changes start a new group, or null. The
   * caller sorts by it first; this only draws a header row at each change.
   */
  groupByField?: string | null;
  /**
   * Called when a row is clicked. A page writes `@row-click`, and Vue binds it to this prop.
   *
   * This is a prop and not an emit on purpose. The table must know whether any page listens.
   * When no page listens, rows get no pointer cursor and no click handling.
   * A declared prop is `undefined` when nobody listens, so the table can tell.
   * A listener for an entry in `defineEmits` cannot be detected this way.
   * Do not add `row-click` to `defineEmits`. Vue would then route `@row-click` to the emit,
   * this prop would always be `undefined`, and every row would quietly stop opening.
   */
  onRowClick?: (row: Record<string, unknown>, event: MouseEvent) => void;
  /**
   * Whether a row can be opened. A row for which this returns false gets no pointer
   * cursor, and a click on it does nothing. Unset means every row can be opened.
   */
  isRowOpenable?: (row: Record<string, unknown>) => boolean;
}>();

const emit = defineEmits<{
  'columnsReordered': [columns: TableColumn[]]
  'columnsResized': [columnId: string, width: number]
  'columnsSorted': [columnId: string]
  /** The header checkbox was clicked. */
  'toggle-page': []
  /** A row checkbox was clicked. The event carries the shift key, so the page can tick a range. */
  'toggle-row': [row: Record<string, unknown>, event: MouseEvent]
}>()

/**
 * The highlighted row: the one whose record shows below the table or in the peek.
 * Ids are compared as text, because a page may hold the id as "12" and the row as 12.
 */
function isActiveRow(row: unknown): boolean {
  if (!rowIdKey || selectedRowId == null) return false
  const rowId = (row as Record<string, unknown>)[rowIdKey]
  return rowId != null && String(rowId) === String(selectedRowId)
}

/**
 * The width of the checkbox column.
 * The pinned columns' sticky offset starts after it, so they never slide over it.
 */
const SELECTION_COLUMN_WIDTH_PX = 40

const selectionColumnStyle = {
  width: `${SELECTION_COLUMN_WIDTH_PX}px`,
  minWidth: `${SELECTION_COLUMN_WIDTH_PX}px`,
  maxWidth: `${SELECTION_COLUMN_WIDTH_PX}px`,
  left: '0px',
}

/** Every data column and filler column, plus the checkbox column when there is one. */
const totalColumnCount = computed(() => allVisibleColumns.value.length + 1 + (selectable ? 1 : 0))

const tableContainerRef = ref<HTMLElement | null>(null)

/** A row is clickable when a page listens, the row is shown, and the page has not ruled it out. */
function isRowClickable(row: Record<string, unknown>): boolean {
  if (!onRowClick) return false
  if (isTableBodyRowHidden?.(row)) return false
  return isRowOpenable?.(row) !== false
}

// The cells read this to know whether a click on their row opens it, so the ID cell can drop its duplicate side-peek button.
provide(ROW_OPENS_IN_PEEK, isRowClickable)

function onBodyRowClick(row: Record<string, unknown>, event: MouseEvent) {
  if (!isRowClickable(row)) return
  const rowElement = event.currentTarget as Element
  const action = classifyRowClick(event, rowElement, { selectable })
  if (action === 'toggle') {
    emit('toggle-row', row, event)
  } else if (action === 'open') {
    onRowClick?.(row, event)
  }
}

/**
 * A shift-click on a row of a table with checkboxes ticks a range of rows.
 * The browser would also paint a text range from the last click to this one.
 * Preventing the mousedown stops that. Interactive elements keep their own mousedown.
 */
function onBodyRowMousedown(row: Record<string, unknown>, event: MouseEvent) {
  if (!selectable || !event.shiftKey || !isRowClickable(row)) return
  const target = event.target
  if (target instanceof Element && target.closest(ROW_CLICK_IGNORE_SELECTOR)) return
  event.preventDefault()
}

// Reveal body rows in chunks across animation frames instead of one synchronous pass, so a
// large/heavy result set fills in while the page stays responsive rather than freezing the UI
// on load. Restarts whenever `data` changes (new results / re-mount). See useProgressiveRows.
const { visibleRows } = useProgressiveRows(() => data)

/** Row index → group header label, when the list is grouped. */
const groupHeaders = computed(() =>
  groupHeadersFor(visibleRows.value as Record<string, unknown>[], groupByField, allVisibleColumns.value),
)

const resizeHandler = (columnId: string, width: number) => {
  emit('columnsResized', columnId, width);
};

/**
 * Track Matching groups each live track with its candidate match rows below
 * it. A row whose `source` is anything other than 'Live Track' is a match
 * candidate that belongs under the live-track row above it.
 */
function isMatchRow(row: Record<string, unknown>): boolean {
  return Boolean(row.source) && row.source !== 'Live Track';
}

/** Max width from elements that overflow their box (truncation, nested flex, etc.). */
function maxOverflowScrollWidth(root: HTMLElement): number {
  let maxWidth = 0
  const consider = (el: HTMLElement) => {
    if (el.scrollWidth > el.clientWidth) {
      maxWidth = Math.max(maxWidth, el.scrollWidth)
    }
  }
  consider(root)
  root.querySelectorAll<HTMLElement>('*').forEach(consider)
  return maxWidth
}

/**
 * Measure how wide cell *content* wants to be, ignoring the live column width.
 * Cloning the node keeps `w-full` / `flex-1` / `truncate` / `min-w-0` classes so the
 * clone still stretches to the current column — we re-measure from innerHTML instead.
 */
function measureIntrinsicInnerWidth(cell: HTMLElement): number {
  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText =
    'position:absolute;left:-9999px;top:0;opacity:0;pointer-events:none;z-index:-1;overflow:visible;'

  const inner = document.createElement('div')
  inner.innerHTML = cell.innerHTML

  const relaxForIntrinsicWidth = (el: HTMLElement) => {
    el.style.setProperty('max-width', 'none', 'important')
    el.style.setProperty('width', 'max-content', 'important')
    el.style.setProperty('min-width', '0', 'important')
    el.style.setProperty('overflow', 'visible', 'important')
    el.style.setProperty('flex', 'none', 'important')
    el.style.setProperty('flex-grow', '0', 'important')
    el.style.setProperty('flex-shrink', '0', 'important')
    el.style.setProperty('flex-basis', 'auto', 'important')
    el.style.setProperty('white-space', 'nowrap', 'important')
    el.classList.remove('truncate', 'min-w-0')
  }

  relaxForIntrinsicWidth(inner)
  inner.querySelectorAll<HTMLElement>('*').forEach(relaxForIntrinsicWidth)

  host.appendChild(inner)
  document.body.appendChild(host)

  const contentW = Math.max(inner.scrollWidth, inner.getBoundingClientRect().width)

  document.body.removeChild(host)
  return contentW
}

/** Measure intrinsic width of a table cell (ignores current column width constraints). */
function measureCellContentWidth(cell: HTMLElement): number {
  const intrinsicInner = measureIntrinsicInnerWidth(cell)
  const cs = getComputedStyle(cell)
  const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)
  const borderX = parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth)
  const innerPlusPaddingAndBorder = intrinsicInner + padX + borderX

  const fromOverflow = maxOverflowScrollWidth(cell)
  return Math.max(MIN_COLUMN_WIDTH_PX, Math.ceil(innerPlusPaddingAndBorder), fromOverflow)
}

const autoSizeColumnHandler = (columnId: string) => {
  void nextTick(() => {
    const root = tableContainerRef.value
    if (!root) return
    const escapedId = CSS.escape(columnId)
    // Body cells only — header (label, sort) is intentionally ignored so width follows data.
    const cells = root.querySelectorAll<HTMLElement>(
      `tbody td[data-column-id="${escapedId}"]`,
    )
    if (cells.length === 0) return

    let contentWidth = 0
    cells.forEach((cell) => {
      contentWidth = Math.max(contentWidth, measureCellContentWidth(cell))
    })
    resizeHandler(columnId, contentWidth)
  })
}

const reorderHandler = (draggedColumnId: string, targetColumnId: string, isPinned: boolean) => {
  const draggedColumn = unref(allVisibleColumns).find(col => col.id === draggedColumnId)
  const targetColumn = unref(allVisibleColumns).find(col => col.id === targetColumnId)
  
  if (!draggedColumn || !targetColumn) {
    return
  }
  
  if (draggedColumn.isPinned !== targetColumn.isPinned) {
    return
  }
  
  const columnList = isPinned ? visiblePinnedColumns.value : visibleColumns.value
  const draggedIndex = columnList.findIndex(col => col.id === draggedColumnId)
  const targetIndex = columnList.findIndex(col => col.id === targetColumnId)

  if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) { 
    return 
  }

  const reorderedColumns = [...columnList]
  const [movedColumn] = reorderedColumns.splice(draggedIndex, 1)
  reorderedColumns.splice(targetIndex, 0, movedColumn)

  // Build new order values for the reordered visible list only
  const newOrderByColumnId: Record<string, number> = {}
  reorderedColumns.forEach((col, index) => {
    newOrderByColumnId[col.id] = isPinned ? -(index + 1) : (index + 1)
  })

  // Emit the full column list with updated order so hidden columns are not lost in the store
  const finalColumns = allColumns.value.map(col => {
    const newOrder = newOrderByColumnId[col.id]
    if (newOrder !== undefined) {
      return { ...col, order: newOrder }
    }
    return col
  })

  emit('columnsReordered', finalColumns)
}

const sortHandler = (columnField: string) => {
  emit('columnsSorted', columnField);
}

/** Lock width so `table-fixed` + full-width table does not redistribute slack when visible columns change. */
const getColumnWidthLockStyle = (column: TableColumn) => {
  const w = `${column.size}px`;
  return {
    width: w,
    minWidth: w,
    maxWidth: w,
  };
};

const getColumnStyle = (column: TableColumn, isPinned: boolean) => {
  const widthLock = getColumnWidthLockStyle(column);

  if (!isPinned) return widthLock

  const pinnedColumns = unref(visiblePinnedColumns);
  const columnIndex = pinnedColumns.findIndex(col => col.id === column.id);
  const leftPosition = pinnedColumns
    .slice(0, columnIndex)
    .reduce((sum, col) => sum + col.size, selectable ? SELECTION_COLUMN_WIDTH_PX : 0);

  const isLastPinned = pinnedColumns[pinnedColumns.length - 1]?.id === column.id;

  return {
    ...widthLock,
    left: `${leftPosition}px`,
    boxShadow: isLastPinned ? 'inset -2px 0 2px -2px var(--border)' : undefined,
  };
};

const visibleColumns = computed(() => {
  return unref(columns).filter(col => col.isVisible);
});

const visiblePinnedColumns = computed(() => {
  return unref(pinnedColumns).filter(col => col.isVisible );
});

const allVisibleColumns = computed(() => {
  return [...visiblePinnedColumns.value, ...visibleColumns.value];
});

/** Full column list (pinned then unpinned) for emitting state updates without losing hidden columns */
const allColumns = computed(() => [
  ...unref(pinnedColumns),
  ...unref(columns),
]);
</script>

<template>
  <div ref="tableContainerRef" :class="['w-full', !tableInParentScroll && 'px-4']">
    <!-- No outline and no row lines, as in Macro: rows are told apart by spacing and a hover tint. -->
    <Table
      :no-scroll-container="tableInParentScroll"
    >
      <colgroup>
        <col v-if="selectable" key="colgroup-selection" :style="selectionColumnStyle" />
        <col
          v-for="column in allVisibleColumns"
          :key="'colgroup-' + column.id"
          :style="getColumnWidthLockStyle(column)"
        />
        <!-- Absorbs horizontal slack so the table stays full-width without resizing data columns. -->
        <col />
      </colgroup>
      <TableHeader :class="[
        'bg-background',
        stickyHeader
          ? 'sticky top-0 z-20'
          : '',
      ]"
      >
        <TableRow>
          <!-- Sticky at the left edge, so it stays put when the table scrolls sideways. -->
          <TableHead
            v-if="selectable"
            class="sticky top-0 z-30 bg-background pl-3"
            :style="selectionColumnStyle"
            data-testid="data-table-select-page"
          >
            <Checkbox
              :model-value="pageSelectionState"
              aria-label="Select every row on this page"
              @click="emit('toggle-page')"
            />
          </TableHead>
          <TableHead
            v-for="column in visiblePinnedColumns"
            :key="'pinned-' + column.id"
            :column-id="column.id"
            :class="[
              'sticky top-0 z-30 overflow-visible bg-background',
            ]"
            :style="getColumnStyle(column, true)"
          >
            <ColumnHeader
              :column-id="column.id"
              :name="column.header"
              :sort-direction="column.sortDirection"
              :width="column.size"
              :isSortable="column.isSortable"
              :isPinned="column.isPinned"
              :resizable="!column.fixedWidth"
              :isVisible="column.isVisible"
              @reorder="(draggedId, targetId) => reorderHandler(draggedId, targetId, column.isPinned)"
              @sort="sortHandler(column.field)"
              @resize="resizeHandler(column.id, $event)"
              @auto-size="autoSizeColumnHandler(column.id)"
            />
          </TableHead>

          <TableHead
            v-for="column in visibleColumns"
            :key="column.id"
            :column-id="column.id"
            :class="[
              'overflow-visible',
              stickyHeader &&
                'sticky top-0 z-10 bg-background',
            ]"
            :style="getColumnStyle(column, false)"
          >
            <ColumnHeader
              :column-id="column.id"
              :name="column.header"
              :sort-direction="column.sortDirection"
              :width="column.size"
              :isSortable="column.isSortable"
              :isPinned="column.isPinned"
              :resizable="!column.fixedWidth"
              :isVisible="column.isVisible"
              @reorder="(draggedId, targetId) => reorderHandler(draggedId, targetId, column.isPinned)"
              @sort="sortHandler(column.field)"
              @resize="resizeHandler(column.id, $event)"
              @auto-size="autoSizeColumnHandler(column.id)"
            />
          </TableHead>
          <TableHead
            aria-hidden="true"
            class="h-10 w-full min-w-0 overflow-hidden bg-background p-0"
          />
        </TableRow>
      </TableHeader>

      <TableBody>
        <template v-if="isLoading">
          <TableRow v-for="i in 20" :key="'skeleton-' + i">
            <TableCell
              v-if="selectable"
              class="sticky z-1 bg-background pl-3"
              :style="selectionColumnStyle"
            >
              <Skeleton class="size-4" />
            </TableCell>
            <TableCell
              v-for="column in allVisibleColumns"
              :key="'skeleton-cell-' + column.id"
              :column-id="column.id"
              :class="[column.isPinned && 'sticky z-1 bg-background']"
              :style="getColumnStyle(column, column.isPinned)"
            >
              <Skeleton class="h-4 w-3/4" />
            </TableCell>
            <TableCell
              aria-hidden="true"
              class="w-full min-w-0 overflow-hidden p-2"
            />
          </TableRow>
        </template>
        <template v-else-if="data?.length">
          <template
            v-for="(row, index) in visibleRows"
            :key="(row as Record<string, unknown>).id != null ? String((row as Record<string, unknown>).id) : `row-${index}`"
          >
          <TableRow v-if="groupHeaders.has(index)" data-testid="data-table-group-header" class="hover:bg-transparent">
            <TableCell :colspan="totalColumnCount" class="px-0 py-0.5">
              <ListGroupHeader :label="groupHeaders.get(index)!" />
            </TableCell>
          </TableRow>
          <TableRow
            :class="[
              // EVERY row background here is opaque, and that is load-bearing rather
              // than a style choice. A pinned cell is sticky: it needs an opaque
              // backdrop or the columns scrolling underneath show through it. Painting
              // that backdrop on the cell itself made the cell opt out of every
              // row-level background — hover, selection and the match highlight all
              // stopped dead at the pinned column — so the cell inherits from the row
              // instead, and a row tint with any alpha in it becomes a see-through
              // pinned column.
              //
              // These mixes are exact, not approximations: the table sits directly on
              // --background (verified), so `muted/50` over the row and
              // `color-mix(muted 50%, background)` resolve to the same colour. If this
              // table is ever placed on a different backdrop, they stop matching and
              // this is the comment that explains why.
              'bg-background transition-colors hover:bg-[color-mix(in_oklab,var(--muted)_50%,var(--background))]',
              isTableBodyRowHidden?.(row) ? 'hidden' : '',
              !isTableBodyRowHidden?.(row) && isMatchRow(row)
                ? 'text-secondary-foreground bg-[color-mix(in_oklab,var(--secondary)_60%,var(--background))] hover:bg-[color-mix(in_oklab,var(--secondary)_40%,var(--background))]'
                : '',
              // A tint the pinned cells inherit, and no ring: a sticky pinned cell
              // sits above the row and hid the ring's left part.
              isActiveRow(row)
                ? 'bg-[color-mix(in_oklab,var(--primary)_16%,var(--background))] hover:bg-[color-mix(in_oklab,var(--primary)_22%,var(--background))]'
                : '',
              isRowMissing?.(row) ? 'opacity-45' : '',
              isRowClickable(row) ? 'cursor-pointer' : '',
            ]"
            :data-open-row="isActiveRow(row) ? 'true' : undefined"
            :data-live-poll-missing="isRowMissing?.(row) ? 'true' : undefined"
            :aria-selected="isActiveRow(row) ? 'true' : undefined"
            :data-active-row="isActiveRow(row) ? 'true' : undefined"
            :title="isRowMissing?.(row) ? 'No longer found — run the search again to refresh this list.' : undefined"
            @click="(event: MouseEvent) => onBodyRowClick(row, event)"
            @mousedown="(event: MouseEvent) => onBodyRowMousedown(row, event)"
          >
            <!-- bg-inherit for the same reason as the pinned cells below. -->
            <TableCell
              v-if="selectable"
              class="sticky z-1 bg-inherit pl-3"
              :style="selectionColumnStyle"
              data-testid="data-table-select-row"
              data-row-click-ignore
            >
              <Checkbox
                :model-value="isRowSelected?.(row) ?? false"
                :aria-label="`Select row ${String((row as Record<string, unknown>).id ?? index + 1)}`"
                @click="(event: MouseEvent) => emit('toggle-row', row as Record<string, unknown>, event)"
              />
            </TableCell>
            <TableCell
              v-for="(column, columnIndex) in allVisibleColumns"
              :key="column.id"
              :column-id="column.id"
              :class="[
                // bg-inherit, not bg-background: it takes whatever the row is painted
                // with, so the pinned column follows hover, selection and the match
                // highlight instead of staying stubbornly plain.
                column.isPinned && 'sticky z-1 bg-inherit',
                columnIndex === 0 && isMatchRow(row) ? 'pl-6' : '',
              ]"
              :style="getColumnStyle(column, column.isPinned)"
            >
              <slot name="cell" :column="column" :row="row" :row-index="index">
                <ColumnMapping
                  :field="column.key"
                  :dataType="column.dataType"
                  :data="row"
                />
              </slot>
            </TableCell>
            <TableCell
              aria-hidden="true"
              class="w-full min-w-0 overflow-hidden p-2"
            />
          </TableRow>
          </template>
        </template>
        <TableRow v-else>
          <TableCell
            :colspan="totalColumnCount"
            class="h-24 text-center text-muted-foreground"
          >
            No results.
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
